import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { FirebaseError } from "firebase/app";
import { createUserWithEmailAndPassword, deleteUser, reload, sendEmailVerification, signOut, updateProfile, validatePassword } from "firebase/auth";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { useEffect, useState } from "react";
import { CheckCircle2, Eye, EyeOff, LockKeyhole, LogOut, Mail, RefreshCw, UserRound } from "lucide-react";
import { auth, db, emulatorConfig } from "../firebase/config";
import { useAuth } from "./AuthProvider";
async function checkPassword(password) {
    if (emulatorConfig.enabled) {
        const meetsMinPasswordLength = password.length >= 6;
        return {
            isValid: meetsMinPasswordLength,
            meetsMinPasswordLength,
            passwordPolicy: {
                customStrengthOptions: { minPasswordLength: 6 },
                allowedNonAlphanumericCharacters: "",
                enforcementState: "ENFORCE",
                forceUpgradeOnSignin: false,
            },
        };
    }
    return validatePassword(auth, password);
}
function registrationError(error) {
    if (!(error instanceof FirebaseError))
        return "Your account could not be created. Please try again.";
    if (error.code === "auth/email-already-in-use")
        return "An account already exists for this email address.";
    if (error.code === "auth/invalid-email")
        return "Enter a valid email address.";
    if (error.code === "auth/weak-password" || error.code === "auth/password-does-not-meet-requirements")
        return "Your password does not meet the current security requirements.";
    if (error.code === "auth/too-many-requests")
        return "Too many verification emails were requested. Please wait and try again.";
    if (error.code === "auth/network-request-failed")
        return "The database network could not be reached.";
    return "Your account could not be created. Please try again.";
}
function passwordRequirementItems(status) {
    if (!status)
        return [];
    const options = status.passwordPolicy.customStrengthOptions;
    const requirements = [];
    if (typeof options.minPasswordLength === "number")
        requirements.push({ label: `At least ${options.minPasswordLength} characters`, met: status.meetsMinPasswordLength === true });
    if (options.containsUppercaseLetter)
        requirements.push({ label: "One uppercase letter", met: status.containsUppercaseLetter === true });
    if (options.containsLowercaseLetter)
        requirements.push({ label: "One lowercase letter", met: status.containsLowercaseLetter === true });
    if (options.containsNumericCharacter)
        requirements.push({ label: "One number", met: status.containsNumericCharacter === true });
    if (options.containsNonAlphanumericCharacter)
        requirements.push({ label: "One special character", met: status.containsNonAlphanumericCharacter === true });
    if (status.meetsMaxPasswordLength === false && typeof options.maxPasswordLength === "number")
        requirements.push({ label: `No more than ${options.maxPasswordLength} characters`, met: false });
    return requirements;
}
function verificationSettings() {
    return { url: `${window.location.origin}/register`, handleCodeInApp: false };
}
async function createVerifiedUserDocument(user) {
    const userRef = doc(db, "users", user.uid);
    const existing = await getDoc(userRef);
    if (existing.exists())
        return;
    const authCreated = Date.parse(user.metadata.creationTime ?? "");
    const created = Number.isFinite(authCreated) ? authCreated : Date.now();
    await setDoc(userRef, {
        coach: true,
        created,
        email: user.email ?? "",
        enabled: true,
        id: user.uid,
        lastLogin: Date.now(),
        name: user.displayName ?? "User",
        subscription: -2,
        subscriptionEnds: created + 14 * 24 * 60 * 60 * 1000,
    });
}
export function RegistrationScreen() {
    const { user } = useAuth();
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [passwordStatus, setPasswordStatus] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [verificationPending, setVerificationPending] = useState(Boolean(user && !user.emailVerified));
    const [verificationMessage, setVerificationMessage] = useState("");
    const [checkingVerification, setCheckingVerification] = useState(false);
    const [resendSeconds, setResendSeconds] = useState(0);
    useEffect(() => {
        if (user && !user.emailVerified) {
            setVerificationPending(true);
            setEmail(user.email ?? "");
        }
    }, [user]);
    useEffect(() => {
        if (resendSeconds <= 0)
            return;
        const timeout = window.setTimeout(() => setResendSeconds((seconds) => Math.max(0, seconds - 1)), 1000);
        return () => window.clearTimeout(timeout);
    }, [resendSeconds]);
    useEffect(() => {
        let active = true;
        const timeout = window.setTimeout(() => {
            void checkPassword(password).then((status) => {
                if (active)
                    setPasswordStatus(status);
            }).catch(() => {
                if (active)
                    setPasswordStatus(null);
            });
        }, password ? 180 : 0);
        return () => {
            active = false;
            window.clearTimeout(timeout);
        };
    }, [password]);
    async function register(event) {
        event.preventDefault();
        setSubmitting(true);
        setError("");
        const name = `${firstName.trim()} ${lastName.trim()}`.trim();
        let createdUser = null;
        try {
            const validation = await checkPassword(password);
            setPasswordStatus(validation);
            if (!validation.isValid) {
                setError("Your password must meet every security requirement below.");
                return;
            }
            const credential = await createUserWithEmailAndPassword(auth, email.trim(), password);
            createdUser = credential.user;
            await updateProfile(createdUser, { displayName: name });
            await sendEmailVerification(createdUser, verificationSettings());
            setVerificationPending(true);
            setResendSeconds(30);
            setVerificationMessage("Verification email sent. Open the link in the email, then return here.");
        }
        catch (caughtError) {
            if (createdUser) {
                try {
                    await deleteUser(createdUser);
                }
                catch { /* The incomplete account can be cleaned up administratively. */ }
            }
            setError(registrationError(caughtError));
        }
        finally {
            setSubmitting(false);
        }
    }
    async function checkVerification() {
        const currentUser = auth.currentUser;
        if (!currentUser) {
            setError("Your verification session expired. Please sign in again.");
            return;
        }
        setCheckingVerification(true);
        setError("");
        setVerificationMessage("");
        try {
            await reload(currentUser);
            if (!currentUser.emailVerified) {
                setVerificationMessage("Your email is not verified yet. Open the verification link and try again.");
                return;
            }
            await currentUser.getIdToken(true);
            await createVerifiedUserDocument(currentUser);
            window.location.replace("/");
        }
        catch {
            setError("Your verification status could not be checked. Please try again.");
        }
        finally {
            setCheckingVerification(false);
        }
    }
    async function resendVerification() {
        const currentUser = auth.currentUser;
        if (!currentUser || resendSeconds > 0)
            return;
        setError("");
        setVerificationMessage("");
        try {
            await sendEmailVerification(currentUser, verificationSettings());
            setResendSeconds(30);
            setVerificationMessage("A new verification email was sent.");
        }
        catch (caughtError) {
            setError(registrationError(caughtError));
        }
    }
    if (verificationPending) {
        return _jsxs("main", { className: "login-page", children: [_jsxs("section", { className: "login-story", "aria-label": "My Track and Field Team", children: [_jsx("div", { className: "login-story__track", "aria-hidden": "true" }), _jsxs("div", { className: "login-brand", children: [_jsx("div", { className: "brand__mark", children: _jsx("img", { src: "/img/logo.png", alt: "" }) }), _jsx("strong", { children: "MY TRACK & FIELD TEAM" })] }), _jsxs("div", { className: "login-story__copy", children: [_jsx("span", { className: "login-kicker", children: "ONE LAST STEP" }), _jsxs("h1", { children: ["Verify your email.", _jsx("br", {}), "Then build your team."] }), _jsx("p", { children: "Your account remains locked until your email address is confirmed." })] }), _jsx("p", { className: "login-story__footer", children: "Track & field and cross country team management" })] }), _jsx("section", { className: "login-panel", children: _jsxs("div", { className: "login-form-wrap registration-verification", children: [_jsxs("div", { className: "login-mobile-brand", children: [_jsx("div", { className: "brand__mark", children: _jsx("img", { src: "/img/logo.png", alt: "" }) }), _jsx("strong", { children: "MY TRACK & FIELD TEAM" })] }), _jsx("div", { className: "registration-verification__icon", children: _jsx(Mail, { size: 25 }) }), _jsx("span", { className: "login-kicker", children: "CHECK YOUR INBOX" }), _jsx("h2", { children: "Verify your email address" }), _jsxs("p", { children: ["We sent a verification link to ", _jsx("strong", { children: user?.email ?? email }), ". You cannot continue to your account until verification is complete. If you do not see the email, check your spam or junk folder."] }), verificationMessage && _jsxs("div", { className: "registration-verification__message", children: [_jsx(CheckCircle2, { size: 16 }), verificationMessage] }), error && _jsx("div", { className: "login-error", role: "alert", children: error }), _jsx("button", { className: "login-submit", type: "button", disabled: checkingVerification, onClick: () => void checkVerification(), children: checkingVerification ? _jsxs(_Fragment, { children: [_jsx(RefreshCw, { className: "spin", size: 16 }), " Checking..."] }) : "I verified my email" }), _jsx("button", { className: "registration-resend", type: "button", disabled: resendSeconds > 0, onClick: () => void resendVerification(), children: resendSeconds > 0 ? `Resend available in ${resendSeconds}s` : "Resend verification email" }), _jsxs("p", { className: "registration-verification__support", children: ["Unable to verify your account? ", _jsx("a", { href: "mailto:support@casillassportsapps.com", children: "Contact support" })] }), _jsxs("button", { className: "registration-signout", type: "button", onClick: () => void signOut(auth), children: [_jsx(LogOut, { size: 15 }), " Use a different account"] })] }) })] });
    }
    const passwordRequirements = passwordRequirementItems(passwordStatus);
    const minimumPasswordLength = passwordStatus?.passwordPolicy.customStrengthOptions.minPasswordLength ?? 8;
    const maximumPasswordLength = passwordStatus?.passwordPolicy.customStrengthOptions.maxPasswordLength;
    return _jsxs("main", { className: "login-page", children: [_jsxs("section", { className: "login-story", "aria-label": "My Track and Field Team", children: [_jsx("div", { className: "login-story__track", "aria-hidden": "true" }), _jsxs("div", { className: "login-brand", children: [_jsx("div", { className: "brand__mark", children: _jsx("img", { src: "/img/logo.png", alt: "" }) }), _jsx("strong", { children: "MY TRACK & FIELD TEAM" })] }), _jsxs("div", { className: "login-story__copy", children: [_jsx("span", { className: "login-kicker", children: "GET STARTED" }), _jsxs("h1", { children: ["Build your team.", _jsx("br", {}), "Start tracking progress."] }), _jsx("p", { children: "Create your coach account and begin your two-week trial." })] }), _jsx("p", { className: "login-story__footer", children: "Track & field and cross country team management" })] }), _jsx("section", { className: "login-panel", children: _jsxs("div", { className: "login-form-wrap registration-form-wrap", children: [_jsxs("div", { className: "login-mobile-brand", children: [_jsx("div", { className: "brand__mark", children: _jsx("img", { src: "/img/logo.png", alt: "" }) }), _jsx("strong", { children: "MY TRACK & FIELD TEAM" })] }), _jsx("span", { className: "login-kicker", children: "CREATE ACCOUNT" }), _jsx("h2", { children: "Start your free trial" }), _jsx("p", { children: "Enter your information to create a coach account." }), _jsxs("form", { className: "login-form registration-form", onSubmit: register, children: [_jsxs("div", { className: "registration-name-row", children: [_jsxs("label", { children: ["First name", _jsxs("div", { className: "input-wrap", children: [_jsx(UserRound, { size: 18 }), _jsx("input", { placeholder: "First", value: firstName, onChange: (event) => setFirstName(event.target.value), autoComplete: "given-name", maxLength: 40, required: true })] })] }), _jsxs("label", { children: ["Last name", _jsxs("div", { className: "input-wrap", children: [_jsx(UserRound, { size: 18 }), _jsx("input", { placeholder: "Last", value: lastName, onChange: (event) => setLastName(event.target.value), autoComplete: "family-name", maxLength: 40, required: true })] })] })] }), _jsx("label", { htmlFor: "register-email", children: "Email address" }), _jsxs("div", { className: "input-wrap", children: [_jsx(Mail, { size: 18 }), _jsx("input", { id: "register-email", type: "email", placeholder: "Enter a valid email", value: email, onChange: (event) => setEmail(event.target.value), autoComplete: "email", required: true })] }), _jsx("label", { htmlFor: "register-password", children: "Password" }), _jsxs("div", { className: "input-wrap", children: [_jsx(LockKeyhole, { size: 18 }), _jsx("input", { id: "register-password", type: showPassword ? "text" : "password", placeholder: "Enter a password", value: password, onChange: (event) => { setPassword(event.target.value); setError(""); }, autoComplete: "new-password", minLength: minimumPasswordLength, maxLength: maximumPasswordLength, "aria-describedby": "registration-password-requirements", required: true }), _jsx("button", { type: "button", className: "password-toggle", onClick: () => setShowPassword((visible) => !visible), "aria-label": showPassword ? "Hide password" : "Show password", children: showPassword ? _jsx(EyeOff, { size: 18 }) : _jsx(Eye, { size: 18 }) })] }), passwordRequirements.length > 0 && _jsx("div", { className: "registration-password-requirements", id: "registration-password-requirements", "aria-label": "Password requirements", children: passwordRequirements.map((requirement) => _jsxs("span", { className: requirement.met ? "met" : "", children: [_jsx("i", { "aria-hidden": "true", children: requirement.met ? "✓" : "" }), requirement.label] }, requirement.label)) }), error && _jsx("div", { className: "login-error", role: "alert", children: error }), _jsx("button", { className: "login-submit", type: "submit", disabled: submitting, children: submitting ? "Creating account..." : "Create account" })] }), _jsxs("p", { className: "login-switch", children: ["Already have an account? ", _jsx("a", { href: "/", children: "Sign in" })] }), _jsxs("p", { className: "login-consent", children: ["By continuing, you accept our ", _jsx("a", { href: "https://casillassportsapps.com/terms-of-service/", target: "_blank", rel: "noreferrer", children: "Terms of Service" }), " and ", _jsx("a", { href: "https://casillassportsapps.com/privacy-policy/", target: "_blank", rel: "noreferrer", children: "Privacy Policy" }), "."] })] }) })] });
}
